/**
 * "From the bucket" in "Upload data" (TRA-1559): staff register a set too
 * large for one upload from the team's staging folder in the data bucket.
 * They copy its two files there with gcloud, or on a stack that signs
 * uploads pick them here and the browser sends them straight to the folder.
 * The api checks every row where the files are, in the background, and the
 * form follows the check until the set is published or refused.
 */

import { useId, useState } from 'react'

import type { TrainingSetStagingOption } from '@/api/generated/model'
import { getApiErrorDetail } from '@/api/client'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { DialogFooter } from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useRegisterTrainingSet, useSignStagingUpload, useTrainingSetRegistration } from '@/hooks/useTrainingRuns'

import { gcloudCommand, parseRegisterSet, registrationLine, sendToStaging, STAGED_FILES, stagingFolder } from './upload'
import type { UploadSetErrors } from './upload'

type StagedFile = (typeof STAGED_FILES)[number]

export function RegisterSetForm({
  staging,
  onCancel,
}: {
  /** The team's staging folder, from GET /sets; undefined while it loads. */
  staging: TrainingSetStagingOption | undefined
  onCancel: () => void
}): React.JSX.Element {
  const [setId, setSetId] = useState('')
  const [license, setLicense] = useState('')
  const [files, setFiles] = useState<Partial<Record<StagedFile, File>>>({})
  const [errors, setErrors] = useState<UploadSetErrors>({})
  const [refusal, setRefusal] = useState<string | null>(null)
  const [sending, setSending] = useState<string | null>(null)
  const [registered, setRegistered] = useState<string | null>(null)
  const register = useRegisterTrainingSet()
  const sign = useSignStagingUpload()
  const status = useTrainingSetRegistration(registered)
  const ids = { setId: useId(), license: useId(), train: useId(), validation: useId() }
  const checking = status.data?.state === 'checking'
  const pending = sending !== null || register.isPending || checking
  const folderName = setId.trim() === '' ? '<data name>' : setId.trim()

  async function send(name: string): Promise<void> {
    for (const file of STAGED_FILES) {
      const picked = files[file]
      if (picked === undefined) continue
      const signed = await sign.mutateAsync({ data: { set_id: name, file } })
      if (signed.status !== 201) throw new Error(`The api did not sign the upload of ${file}.`)
      await sendToStaging(signed.data, picked, (sent) => {
        setSending(`${file} is ${String(Math.floor((100 * sent) / Math.max(picked.size, 1)))}% sent.`)
      })
    }
  }

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = parseRegisterSet({ setId, license })
    if (parsed.kind === 'error') {
      setErrors(parsed.errors)
      return
    }
    setErrors({})
    setRefusal(null)
    try {
      await send(parsed.form.setId)
      setSending(null)
      await register.mutateAsync({ data: { set_id: parsed.form.setId, recipe: 'sft-chat', license: parsed.form.license } })
      setRegistered(parsed.form.setId)
    } catch (caught) {
      setRefusal(getApiErrorDetail(caught) ?? (caught instanceof Error ? caught.message : 'The data was not registered.'))
    } finally {
      setSending(null)
    }
  }

  const described = (field: 'setId' | 'license'): string | undefined =>
    errors[field] === undefined ? undefined : `${ids[field]}-error`
  const outcome = status.data
  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor={ids.setId}>Data name</Label>
        <Input
          id={ids.setId}
          value={setId}
          placeholder="trajectories-64k-2026-10-27"
          className="font-mono"
          disabled={pending}
          aria-invalid={errors.setId !== undefined}
          aria-describedby={described('setId')}
          onChange={(event) => {
            setSetId(event.target.value)
          }}
        />
        <FieldError id={`${ids.setId}-error`} message={errors.setId} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={ids.license}>License</Label>
        <Textarea
          id={ids.license}
          value={license}
          rows={2}
          placeholder="Private data. Training authorized by its owner."
          disabled={pending}
          aria-invalid={errors.license !== undefined}
          aria-describedby={described('license')}
          onChange={(event) => {
            setLicense(event.target.value)
          }}
        />
        <FieldError id={`${ids.license}-error`} message={errors.license} />
      </div>
      {staging === undefined ? (
        <p className="text-xs text-muted-foreground">The team&apos;s staging folder is loading.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-medium leading-none">Staging folder</p>
          <p className="flex items-center gap-2 break-all font-mono text-xs">
            {stagingFolder(staging, folderName)}
            <CopyButton value={gcloudCommand(staging, folderName)} label="Copy the gcloud command" />
          </p>
          <p className="text-xs text-muted-foreground">
            Copy train.jsonl and validation.jsonl into this folder with gcloud storage cp and register them.
            {staging.signed_uploads ? ' You can also pick them below, and this browser sends them there first.' : ''}
          </p>
        </div>
      )}
      {staging?.signed_uploads === true &&
        STAGED_FILES.map((file) => {
          const id = file === 'train.jsonl' ? ids.train : ids.validation
          return (
            <div key={file} className="space-y-2">
              <Label htmlFor={id}>{file === 'train.jsonl' ? 'Training file (optional)' : 'Validation file (optional)'}</Label>
              <Input
                id={id}
                type="file"
                accept=".jsonl,application/jsonl"
                disabled={pending}
                onChange={(event) => {
                  const picked = event.target.files?.[0]
                  setFiles((current) => ({ ...current, [file]: picked }))
                }}
              />
            </div>
          )
        })}
      {sending !== null && (
        <p role="status" className="text-xs text-muted-foreground">
          {sending}
        </p>
      )}
      {outcome !== undefined && (
        <p
          role={outcome.state === 'refused' ? 'alert' : 'status'}
          className={outcome.state === 'refused' ? 'text-sm text-destructive' : 'text-xs text-muted-foreground'}
        >
          {registrationLine(outcome)}
        </p>
      )}
      {refusal !== null && (
        <p role="alert" className="text-sm text-destructive">
          {refusal}
        </p>
      )}
      <DialogFooter className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {outcome?.state === 'registered' ? 'Close' : 'Cancel'}
        </Button>
        <Button type="submit" disabled={pending || staging === undefined || outcome?.state === 'registered'}>
          {checking ? 'Checking…' : 'Register data'}
        </Button>
      </DialogFooter>
    </form>
  )
}
